import React, { useCallback, useEffect, useState } from "react"
import {
  ActivityIndicator,
  FlatList,
  InteractionManager,
  Platform,
  Pressable,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"

import { Image, ImageStyle } from "expo-image"
import { router, useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { MSKType } from "types/sponsor"

import { CloseButton } from "src/components/CloseButton"
import { Button, ButtonAccessoryProps, Text } from "src/components/Ignite"
import { SidekickView } from "src/components/SidekickView"
import { useAvatarMode } from "src/context/AvatarModeContext"
import { useChat } from "src/context/ChatContext"
import { avatarImageMap } from "src/context/RiveContext"
import { useTyping } from "src/context/TypingContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCurrentMsk, useMSKAvailable, useMskAvailableQuery } from "src/queries/useConfig"
import { useMemberQuery, useUpdateMemberMsk } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { getWebFixedWidthStyle } from "src/utils/platformUtils"
import { backOrToRoot } from "src/utils/routerHelpers"

export default function SelectMSKScreen() {
  const { themed, theme } = useAppTheme()
  const insets = useSafeAreaInsets()
  const [selectedMSK, setSelectedMSK] = useState<string | null>(null)

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )
  const updateMember = useUpdateMemberMsk()
  const mskQuery = useMskAvailableQuery()
  const currentMSK = useCurrentMsk()
  const { setMode } = useAvatarMode()
  const { isSwitchingSidekick } = useChat()
  const { stopSpeech } = useVisemes()
  const memberQuery = useMemberQuery()
  const { awaitingESKResponse } = useTyping()

  const mskTypes = useMSKAvailable()

  // Sort MSK types to show default first
  const sortedMskTypes = React.useMemo(() => {
    return [...mskTypes].sort((a, b) => {
      // Default MSK comes first
      if (a.default && !b.default) return -1
      if (!a.default && b.default) return 1
      // If both have same default status, maintain original order
      return 0
    })
  }, [mskTypes])

  // Select the first sidekick by default when the list is loaded
  useEffect(() => {
    if (selectedMSK === null && sortedMskTypes.length > 0) {
      setSelectedMSK(sortedMskTypes[0].id)
    }
  }, [sortedMskTypes, selectedMSK])

  const renderMSKOption = useCallback(
    ({ item: msk }: { item: MSKType }) => {
      const localImage = avatarImageMap[msk.avatar_id.toString().padStart(2, "0")]

      return (
        <Pressable key={msk.id} style={themed($mskOption)} onPress={() => setSelectedMSK(msk.id)}>
          <View
            style={[
              themed($imageContainer),
              {
                borderWidth: selectedMSK === msk.id ? 4 : 0,
                borderColor: theme.colors.border,
              },
            ]}
          >
            <Image source={localImage} style={$avatarImage} contentFit="cover" />
          </View>
          <Text text={msk.name} style={themed($mskName)} />
        </Pressable>
      )
    },
    [themed, selectedMSK, theme.colors.border],
  )

  const onSelectMSK = useCallback(async () => {
    if (
      selectedMSK &&
      (memberQuery?.data?.new_user || !currentMSK || selectedMSK !== currentMSK.id)
    ) {
      await stopSpeech()
      updateMember.mutate(selectedMSK, {
        onSuccess: () => {
          if (router.canDismiss()) {
            router.dismissTo("/")
          } else {
            router.replace("/")
          }
          // expo-router navigation is fire-and-forget (no Promise). Run setMode after transition.
          // Called in both branches so the avatar always opens in large mode after MSK selection.
          InteractionManager.runAfterInteractions(() => {
            setMode("large", false)
          })
        },
      })
    } else {
      console.log("[SELECT_MSK] No new MSK selected, going back")
      router.dismissTo("/")
    }
  }, [selectedMSK, memberQuery?.data?.new_user, currentMSK, stopSpeech, updateMember, setMode])

  // Use same redirect logic as index.tsx: show avatar/button cluster only when user was NOT
  // redirected here (i.e. they opened select-msk from menu to change sidekick). When they were
  // redirected (needsSelectMsk), hide SidekickView until they complete selection.
  const hasMskInfo = mskQuery.isSuccess && memberQuery.isSuccess
  const hasInvalidMsk = hasMskInfo && !currentMSK
  const newUserValue = memberQuery?.data?.new_user
  const isNewUserUnset = newUserValue === undefined
  const isNewUser = isNewUserUnset || newUserValue
  const needsSelectMsk = hasInvalidMsk || isNewUser

  useEffect(() => {
    console.log(
      "[select-msk] needsSelectMsk:",
      needsSelectMsk,
      "hasInvalidMsk:",
      hasInvalidMsk,
      "isNewUser:",
      isNewUser,
      "currentMSK:",
      currentMSK?.id ?? null,
    )
  }, [needsSelectMsk, hasInvalidMsk, isNewUser, currentMSK?.id])

  return (
    <SidekickView
      showAvatar={!needsSelectMsk}
      autoShowAvatarOnSpeech={!needsSelectMsk}
      showButtonCluster={!needsSelectMsk}
      compactButtonCluster={true}
      darkBackground={true}
    >
      <StatusBar style="light" />
      <View
        style={[themed($header), { top: insets.top, left: insets.left, right: insets.right }]}
        pointerEvents="box-none"
      >
        <Text text="Select Your Personal Sidekick" style={themed($headerTitle)} />
        <CloseButton
          onPress={() => backOrToRoot()}
          accessibilityLabel="Close"
          style={{ position: "absolute", top: 22, right: 16 }}
        />
      </View>
      <View
        style={[
          themed($contentContainer),
          getWebFixedWidthStyle(480),
          { paddingTop: insets.top + 72 },
        ]}
      >
        <View style={themed($gridContainer)}>
          <FlatList
            data={sortedMskTypes}
            renderItem={renderMSKOption}
            keyExtractor={(item) => item.id.toString()}
            numColumns={2}
            columnWrapperStyle={themed($columnWrapper)}
            contentContainerStyle={themed($flatListContent)}
            showsVerticalScrollIndicator={false}
          />
        </View>

        <View style={themed($footer)}>
          <Button
            text="Select"
            preset="sidekick"
            onPress={onSelectMSK}
            disabled={
              selectedMSK === null ||
              updateMember.isPending ||
              isSwitchingSidekick ||
              awaitingESKResponse
            }
            LeftAccessory={(props: ButtonAccessoryProps) =>
              updateMember.isPending ? (
                <ActivityIndicator
                  size="small"
                  color={theme.colors.white}
                  style={[props.style, $selectButtonSpinner]}
                />
              ) : null
            }
          />
        </View>
      </View>
    </SidekickView>
  )
}

const $contentContainer: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})

const $header: ThemedStyle<ViewStyle> = () => ({
  // Anchor to the window (not the width-constrained content column) on web so
  // the close button aligns with the window edge like the other settings views.
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  zIndex: 2100,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.white,
  fontSize: 18,
  fontFamily: typography.fonts.figtree.bold,
  fontWeight: "700",
  textAlign: "center",
})

const $gridContainer: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
  overflow: "hidden",
})

const $columnWrapper: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flex: 1,
  gap: spacing.md,
})

const $flatListContent: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  padding: spacing.md,
})

const $footer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.md,
})

const $selectButtonSpinner: ViewStyle = { marginRight: 8 }

const $mskOption: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flex: 1,
  maxWidth: "50%",
  alignItems: "center",
  marginBottom: spacing.md,
})

const $imageContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: "100%",
  aspectRatio: 1,
  borderRadius: 12,
  backgroundColor: colors.buttonBackground, // Match button cluster grey
  overflow: "hidden",
  justifyContent: "flex-end", // Flush to bottom
})

const $avatarImage: ImageStyle = {
  width: "100%",
  height: "100%",
}

const $mskName: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text, // Use theme text color
  fontFamily: typography.fonts.figtree.normal,
  textAlign: "center",
  fontSize: 14,
  marginTop: 8,
})
